import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import styles from "../../casinoComponents/videoBox/VideoBox.module.css";
import { getCardImage } from "../../casinoComponents/resultVisuals/cardAssets";
import { getCasinoRulesHtml } from "../../../data/casinoRulesHtml";
import { IS_REF_MOBILE } from "../../../utils/refViewport";
import Dum10Owl from "./Dum10Owl";

// The reference's slider uses static/front/img/cards (50x70; img/cards renders 40px tall instead of 44.8),
// its current card static/admin/img/cards like the other overlays
const FRONT_CARDS = `${import.meta.env.BASE_URL}img/cards-front`;
// like the reference, an empty token (new round, lcard "") gives ".png"
const getFrontCardImage = (c) => `${FRONT_CARDS}/${String(c ?? "").trim()}.png`;

const nextFrame = (fn) => requestAnimationFrame(() => requestAnimationFrame(fn));
const FADE_MS = 300; // reference: .base-timer .component-fade-*-active { transition: .3s }

/**
 * One timer digit inside the reference's <transition name="component-fade" mode="out-in">:
 * on change the old digit leaves (component-fade-leave(-active/-to)), then the new one enters
 * (component-fade-enter(-active/-to)) — same class sequence as Vue 2, no animation on first render.
 */
function FadeDigit({ value }) {
  const [shown, setShown] = useState(value);
  const elRef = useRef(null);
  const shownRef = useRef(value);

  useEffect(() => {
    const el = elRef.current;
    if (!el || value === shownRef.current) return undefined;
    let alive = true;
    const timers = [];
    const setCls = (...c) => {
      if (alive) el.className = c.join(" ");
    };
    setCls("component-fade-leave", "component-fade-leave-active");
    nextFrame(() => setCls("component-fade-leave-active", "component-fade-leave-to"));
    timers.push(
      setTimeout(() => {
        if (!alive) return;
        shownRef.current = value;
        setShown(value);
        // Vue enters with a fresh element; this one is reused, so snap to the enter state before transitioning
        setCls("component-fade-enter");
        void el.offsetWidth;
        setCls("component-fade-enter", "component-fade-enter-active");
        nextFrame(() => setCls("component-fade-enter-active", "component-fade-enter-to"));
        timers.push(setTimeout(() => el.removeAttribute("class"), FADE_MS));
      }, FADE_MS)
    );
    return () => {
      alive = false;
      timers.forEach(clearTimeout);
      el.removeAttribute("class");
    };
  }, [value]);

  return <span ref={elRef}>{shown}</span>;
}

const TIMER = { info: "green", warning: { color: "orange", threshold: 6 }, alert: { color: "red", threshold: 3 } };

/** Reference Timer component (.base-timer SVG ring), used by Dus ka Dum instead of the flip clock. */
function BaseTimer({ total, current }) {
  const fraction = current / total;
  const timeFraction = fraction - (1 / total) * (1 - fraction);
  const dash = `${(283 * timeFraction).toFixed(0)} 283`;
  const color =
    current <= TIMER.alert.threshold ? TIMER.alert.color : current <= TIMER.warning.threshold ? TIMER.warning.color : TIMER.info;
  return (
    <div className="base-timer">
      <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" className="base-timer__svg">
        <g className="base-timer__circle">
          <circle cx="50" cy="50" r="45" className="base-timer__path-elapsed" />{" "}
          <path
            strokeDasharray={dash}
            d="M 50, 50 m -45, 0 a 45,45 0 1,0 90,0 a 45,45 0 1,0 -90,0"
            className={`base-timer__path-remaining ${color}`}
          />
        </g>
      </svg>{" "}
      <span className={`base-timer__label ${color}`}>
        {String(current)
          .split("")
          .map((d, i) => (
            <FadeDigit key={i} value={d} />
          ))}
      </span>
    </div>
  );
}

/**
 * Dus ka Dum .casino-video in the reference markup (its own template, not the shared VideoBox):
 *   .casino-video-title (.casino-name, .casino-video-rid, .dkd-total), .video-box-container,
 *   .casino-video-cards.andar-bahar2 (shuffle handle + owl slider of lcard, rtl, 3 items),
 *   .casino-video-current-card, .casino-timer (.base-timer ring, only when ft != 0),
 *   rules (i) icon + rules panel / modal.
 */
export default function Dum10Video({ title, data, iframeSrc, resetKey }) {
  const [streamError, setStreamError] = useState(null);
  const [showCards, setShowCards] = useState(true);
  const [showRules, setShowRules] = useState(false);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const iframeError = streamError?.src === iframeSrc;
  const handleIframeError = (message = "Video stream unavailable") => setStreamError({ src: iframeSrc, message });

  const rulesHtml = getCasinoRulesHtml("dum10");
  // Reference: sliderCards = lcard.split(",").reverse() (newest first; rtl slider)
  const sliderCards = data?.lcard != null ? String(data.lcard).split(",").reverse() : [];
  const total = Number(data?.ft) || 0;
  const current = Number(data?.lt) || 0;

  const handleRulesClick = () => {
    if (!rulesHtml) return;
    setShowRules(true);
    if (IS_REF_MOBILE) setShowRulesModal(true);
  };

  return (
    <div className="casino-video">
      <div className="casino-video-title">
        <span className="casino-name">{title}</span>{" "}
        <div className="casino-video-rid">Round ID: {data?.mid}</div>{" "}
        <div className="dkd-total">
          <div>Current Total:</div> <div className="numeric text-playerb">{data?.csum}</div>
        </div>
      </div>{" "}
      <div className="video-box-container">
        <div className="video-box">
          {iframeError ? (
            <div className={styles.errorMessage}>
              <div>🎥</div>
              <div>{streamError.message}</div>
              <div style={{ fontSize: "0.9rem", marginTop: "0.5rem", opacity: 0.8 }}>
                Please check your connection or try again later
              </div>
            </div>
          ) : (
            <iframe
              src={iframeSrc || undefined}
              title="Live Stream"
              allowFullScreen
              onError={() => handleIframeError()}
              onLoad={(e) => {
                try {
                  const iframeDoc = e.target.contentDocument || e.target.contentWindow?.document;
                  if (iframeDoc?.body?.innerText?.includes("not allowed")) {
                    handleIframeError("Stream access not allowed");
                  }
                } catch {
                  // cross-origin stream: nothing to inspect
                }
              }}
            />
          )}
        </div>
      </div>{" "}
      {sliderCards.length > 0 && (
        <div className={`casino-video-cards andar-bahar2${showCards ? "" : " hide-cards"}`}>
          <div className="casino-cards-shuffle" onClick={() => setShowCards((v) => !v)}>
            <i className="fas fa-grip-lines-vertical" />
          </div>{" "}
          <div className="casino-video-cards-container">
            <Dum10Owl id="andarSlider" className="dum-slider ab-slider owl-carousel owl-theme" rtl items={3} slideBy={4} resetKey={resetKey}>
              {sliderCards.map((c, i) => (
                <div key={`${i}-${c}`} className="item">
                  <span>
                    <img src={getFrontCardImage(c)} alt="" />
                  </span>
                </div>
              ))}
            </Dum10Owl>
          </div>
        </div>
      )}{" "}
      {data?.card ? (
        <div className="casino-video-current-card">
          <div>
            <span>
              <img key={data.card} src={getCardImage(data.card)} alt="" />
            </span>
          </div>
        </div>
      ) : null}{" "}
      <div className="casino-timer">{total !== 0 && <BaseTimer total={total} current={current} />}</div>{" "}
      <div className="casino-video-right-icons">
        <div title="Rules" className="casino-video-rules-icon" onClick={handleRulesClick}>
          <i className="fas fa-info-circle" />
        </div>
      </div>{" "}
      <div>
        {showRules && (
          <div
            id="casino-vieo-rules"
            aria-modal="true"
            className="casino-vieo-rules show d-none-small show-rules"
            style={{ display: "block" }}
          >
            <div className="rules-header">
              <div>Rules</div> <i className="fas fa-times" onClick={() => setShowRules(false)} />
            </div>{" "}
            <div className="rules-body" dangerouslySetInnerHTML={{ __html: rulesHtml }} />
          </div>
        )}
        <Modal
          show={showRulesModal}
          onHide={() => setShowRulesModal(false)}
          className="casino-rules teenpatti2-rule-modal"
          dialogClassName="modal-md"
          id="rules-modal"
        >
          <header className="modal-header">
            <h5 className="modal-title">Rules</h5>{" "}
            <button type="button" aria-label="Close" className="close" onClick={() => setShowRulesModal(false)}>
              ×
            </button>
          </header>
          <Modal.Body>
            <div className="casino-vieo-rules show-rules show">
              <div className="rules-body" dangerouslySetInnerHTML={{ __html: rulesHtml || "" }} />
            </div>
          </Modal.Body>
        </Modal>
      </div>
    </div>
  );
}
